'use client';

import type { Variants } from 'motion/react';
import * as m from 'motion/react-m';
import { useLayoutEffect, useRef, useState } from 'react';
import { SkeletonLine } from './skeletonLine';

// Number of skeleton sections for the scrolling loop
// We render this set twice to create a seamless infinite scroll
const SECTION_COUNT = 6;

// Generate sections with consistent structure
const SECTIONS = Array.from({ length: SECTION_COUNT }, (_, index) => ({
  id: `section-${index}`,
  lineCount: 2 + (index % 2), // Alternating 2-3 lines per section
}));

// Generate consistent line widths based on index (not random per render)
function getLineWidth(sectionIndex: number, lineIndex: number): number {
  const seed = sectionIndex * 10 + lineIndex;
  return 65 + ((seed * 17) % 35); // 65-100% width
}

// Timing constants for infinite scroll
const SECTION_HEIGHT_ESTIMATE = 48; // Approximate height matching Figma - actual is measured at runtime
const SECTION_GAP = 10; // gap-2.5 = 10px (matching Figma)
const SCROLL_START_DELAY = 3.0; // Seconds after drafting starts before scroll begins
const LOOP_DURATION = 15; // Seconds for one complete loop cycle
// Fallback height estimate (used before measurement completes)
const LOOP_HEIGHT_FALLBACK = SECTION_COUNT * (SECTION_HEIGHT_ESTIMATE + SECTION_GAP);

// Stagger timing for section entrance (first 4 visible sections)
const VISIBLE_SECTION_COUNT = 4;
const STAGGER_DELAY = 1.0; // 1s between each section (calmer pace)
const INITIAL_DELAY = 0.5; // 500ms before first section appears

interface SkeletonSectionsProps {
  /** Current phase - controls background warmth, line visibility, and completion state */
  phase: 'building' | 'drafting' | 'complete';
  /** Whether this is the initial mount (for entrance animation) */
  isEntering?: boolean;
}

/**
 * Variants for the stagger container - controls when children animate.
 * Uses Framer Motion's built-in staggerChildren for robust stagger behavior.
 * Only affects children that have `variants` defined - plain divs are ignored.
 */
const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: STAGGER_DELAY,
      delayChildren: INITIAL_DELAY,
    },
  },
};

/**
 * Variants for each visible section - animates from hidden to visible
 */
const sectionVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 12,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 180, // Softer, less bouncy
      damping: 28, // Smoother settling
    },
  },
};

/**
 * Variants for section headers - width grows after section appears
 * Delay of 0.5s ensures section container is clearly visible before header appears
 */
const headerVariants: Variants = {
  hidden: {
    width: '0px',
    opacity: 0,
  },
  visible: {
    width: '64px',
    opacity: 1,
    transition: {
      duration: 0.7, // More gradual reveal
      delay: 0.5, // Noticeable delay after section appears (container first, then content)
      ease: [0.23, 1, 0.32, 1], // ease-out-quint
    },
  },
};

/**
 * One-time shimmer overlay that sweeps across a section on entrance.
 * Uses variants to sync with parent stagger timing.
 */
const shimmerVariants: Variants = {
  hidden: {
    x: '-100%',
    opacity: 0.5,
  },
  visible: {
    x: '200%',
    opacity: 0,
    transition: {
      duration: 1.0, // Slower shimmer sweep
      delay: 0.2,
      ease: 'easeOut',
    },
  },
};

/**
 * Renders a static skeleton section (no entrance animation).
 * Used for below-fold sections and loop duplicates.
 */
function StaticSkeletonSection({
  section,
  sectionIndex,
  isDrafting,
  delayOffset,
  isComplete,
}: {
  section: (typeof SECTIONS)[number];
  sectionIndex: number;
  isDrafting: boolean;
  delayOffset: number;
  isComplete: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-md bg-surface-accent p-2">
      {/* Section header skeleton */}
      <div className="mb-1.5 h-2 w-16 rounded-md bg-background-secondary" />

      {/* Lines container */}
      <div className="flex flex-col gap-1">
        {Array.from({ length: section.lineCount }).map((_, lineIndex) => (
          <SkeletonLine
            key={`${section.id}-line-${lineIndex}`}
            index={lineIndex}
            sectionIndex={sectionIndex}
            widthPercent={getLineWidth(sectionIndex, lineIndex)}
            visible={isDrafting || isComplete}
            delayOffset={delayOffset}
            isComplete={isComplete}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Unified skeleton sections component that persists across building, drafting, and complete phases.
 *
 * Architecture:
 * - The m.div stagger container IS the scrolling flex container (not display:contents)
 * - First 4 sections are rendered as direct m.div children with variants (participate in stagger)
 * - Below-fold and loop sections are plain divs (no variants, ignored by stagger)
 * - This ensures proper Framer Motion orchestration while maintaining layout
 *
 * During drafting, sections scroll infinitely upward using CSS animation.
 * On complete, scroll stops and lines fill to 100% with a glow effect.
 */
export function SkeletonSections({ phase, isEntering = false }: SkeletonSectionsProps) {
  const isDrafting = phase === 'drafting';
  const isComplete = phase === 'complete';

  // Ref to measure the actual rendered height of the first set of sections
  const firstSetRef = useRef<HTMLDivElement>(null);
  const [measuredLoopHeight, setMeasuredLoopHeight] = useState<number | null>(null);

  // Measure the actual height of the first set of sections once drafting starts
  // This ensures the scroll animation loops seamlessly without visible jumps
  useLayoutEffect(() => {
    if (isDrafting && firstSetRef.current && !measuredLoopHeight) {
      // Measure the full height of the first set container (includes gap between sections)
      const height = firstSetRef.current.offsetHeight + SECTION_GAP; // Add one gap for spacing to next set
      setMeasuredLoopHeight(height);
    }
  }, [isDrafting, measuredLoopHeight]);

  // Use measured height for seamless loop, fall back to estimate before measurement
  const effectiveLoopHeight = measuredLoopHeight ?? LOOP_HEIGHT_FALLBACK;

  // CSS custom properties for the infinite scroll animation
  // Stop scrolling when complete - animation pauses at current position
  const scrollStyles =
    isDrafting && !isComplete
      ? ({
          '--loop-height': `${effectiveLoopHeight}px`,
          '--loop-duration': `${LOOP_DURATION}s`,
          '--scroll-delay': `${SCROLL_START_DELAY}s`,
          animationName: 'infinite-scroll',
          animationDuration: 'var(--loop-duration)',
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationDelay: 'var(--scroll-delay)',
          animationFillMode: 'forwards',
        } as React.CSSProperties)
      : isComplete
        ? ({
            // Freeze at current position when complete
            animationPlayState: 'paused',
          } as React.CSSProperties)
        : {};

  return (
    <div className="relative size-full overflow-hidden">
      {/* CSS keyframe for infinite scroll - injected inline for encapsulation */}
      <style>{`
        @keyframes infinite-scroll {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(calc(-1 * var(--loop-height)));
          }
        }
      `}</style>

      {/* 
        Stagger container IS the scrolling flex container.
        - Direct m.div children with variants participate in stagger
        - Plain div children without variants are ignored by stagger
        This fixes the className="contents" issue that broke Framer Motion orchestration.
      */}
      <m.div
        initial={isEntering ? 'hidden' : 'visible'}
        animate="visible"
        variants={containerVariants}
        className="flex flex-col gap-2.5 p-4"
        style={scrollStyles}
      >
        {/* 
          First set of sections - wrapped in a measurable container.
          We measure this container's height to ensure seamless infinite scroll.
          The ref is on a wrapper div that contains all 6 sections of the first set.
        */}
        <div ref={firstSetRef} className="flex flex-col gap-2.5">
          {/* First 4 sections - INLINED as direct m.div children for proper stagger */}
          {SECTIONS.slice(0, VISIBLE_SECTION_COUNT).map((section, sectionIndex) => (
            <m.div
              key={section.id}
              variants={sectionVariants}
              className="relative overflow-hidden rounded-md bg-surface-accent p-2"
            >
              {/* One-time entrance shimmer overlay */}
              <m.div
                variants={shimmerVariants}
                className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              />

              {/* Section header skeleton */}
              <m.div
                variants={headerVariants}
                className="mb-1.5 h-2 rounded-md bg-background-secondary"
              />

              {/* Lines container */}
              <div className="flex flex-col gap-1">
                {Array.from({ length: section.lineCount }).map((_, lineIndex) => (
                  <SkeletonLine
                    key={`${section.id}-line-${lineIndex}`}
                    index={lineIndex}
                    sectionIndex={sectionIndex}
                    widthPercent={getLineWidth(sectionIndex, lineIndex)}
                    visible={isDrafting || isComplete}
                    delayOffset={0}
                    isComplete={isComplete}
                  />
                ))}
              </div>
            </m.div>
          ))}

          {/* Below-fold sections (5-6) - part of the measured first set */}
          {SECTIONS.slice(VISIBLE_SECTION_COUNT).map((section, index) => (
            <StaticSkeletonSection
              key={section.id}
              section={section}
              sectionIndex={index + VISIBLE_SECTION_COUNT}
              isDrafting={isDrafting}
              delayOffset={SCROLL_START_DELAY}
              isComplete={isComplete}
            />
          ))}
        </div>

        {/* Duplicate sections for seamless infinite loop */}
        {SECTIONS.map((section, sectionIndex) => (
          <StaticSkeletonSection
            key={`${section.id}-loop`}
            section={section}
            sectionIndex={sectionIndex}
            isDrafting={isDrafting}
            delayOffset={SCROLL_START_DELAY}
            isComplete={isComplete}
          />
        ))}
      </m.div>
    </div>
  );
}
